/**
 * usePageContext — pages register what's currently on screen so the
 * user's MCP client (driving their browser over BrowserBus) can answer "summarize this", "filter to X", "who is this
 * person" without making the user repeat themselves.
 *
 * Contract for the value you pass in:
 *   - kind: short label, e.g. "bill_detail", "members_index"
 *   - supports: which frontend tools this page can dispatch
 *               ("filter" | "highlight" | "sort" | "navigate")
 *   - visible: snapshot of currently-rendered state. Either an object
 *              or a function returning one. Functions are evaluated at
 *              sync time so the AI always sees fresh state — even
 *              after the user filters/scrolls/hovers AFTER mount.
 *   - focused_entity: hover/select/modal entity in the same fn-or-value
 *              shape. Lets "him", "this bill", "this person" resolve.
 *   - workspace_id / workspace_name: optional override (otherwise the
 *              sync falls back to the layout's current workspace).
 *
 * Cleanup is automatic — the hook clears window.__pageContext when the
 * page unmounts so a stale snapshot from a previous page never leaks.
 *
 * Why a global window var instead of React context: PageContextSync and
 * the BrowserBus choreography dispatcher live OUTSIDE the page subtree
 * (in <Layout> / useBrowserBus) and both read this same snapshot. A
 * global is the simplest cross-boundary surface.
 */
import { useEffect } from "react";

type FnOr<T> = T | (() => T);

export interface PageContextSnapshot {
  kind: string;
  supports?: Array<"filter" | "highlight" | "sort" | "navigate">;
  visible?: FnOr<Record<string, unknown>>;
  focused_entity?: FnOr<{ type?: string; id?: string; name?: string; source?: string } | null>;
  workspace_id?: string | null;
  workspace_name?: string | null;
  /** DashboardPage: whether the layout is AI-composed or the default. */
  dashboard_layout_source?: "ai_composed" | "default";
  /**
   * Hint to the AI that this page is the destination for
   * render_dashboard_panel calls — when the user asks "show me X"
   * here, render INTO the page's feed.
   * The DashboardPage sets this to true.
   */
  panel_feed?: boolean;
  // Choreography handlers (animate_graph_path / set_node_color / etc).
  // Forwarded verbatim so useBrowserBus's dispatcher can call them.
  [handler: `on${string}`]: ((input: any) => any) | undefined;
}

export function usePageContext(snapshot: PageContextSnapshot | null, deps: ReadonlyArray<unknown>) {
  // We intentionally take a deps array instead of a deep object compare
  // so the call site declares precisely when the snapshot's *shape*
  // changes (e.g. new bill_id) — function fields capture the latest
  // closures at re-mount but the deps array is what triggers re-mount.
  useEffect(() => {
    if (!snapshot) return;
    (window as any).__pageContext = snapshot;
    return () => {
      // Only clear if we're still the active context — guards against
      // a slow-unmounting page wiping a freshly-mounted page's snapshot.
      if ((window as any).__pageContext === snapshot) {
        (window as any).__pageContext = undefined;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
